import { observer } from "mobx-react";
import { Box } from "@mui/material";

import PreChatHeader from "../ui/PreChatHeader";
import PreChatContainer from "../ui/PreChatContainer";
import InfoCardGrid from "../ui/InfoCardGrid";
import InfoCard from "../ui/InfoCard";
import BulletList from "../ui/BulletList";
import Column from "../ui/core/Column";
import ChatInput from "../chat/components/ChatInput";
import { channelsConfig } from "../../config/channels";
import DropZoneWrapper from "../ui/DropZoneWrapper";
import { useChatStore } from "../../hooks/useChatStore";
import { PreServiceReviewIcon } from "../icons/PreServiceReviewIcon";

const PreServiceReview: React.FC = observer(() => {
  const chatStore = useChatStore();

  const helpItems = [
    "Document validation",
    "Prior authorization validation and status",
    "Medical necessity documentation review",
    "Coding accuracy and NCCI/MUE validation",
    "Contract coverage and payer compliance",
    "Actionable checklist for any issues found",
  ];

  const scenarios = [
    '"Do the patient, procedure, and authorization match?”',
    '"The PA letter has a different CPT code than the form"',
    '"Does the clinical documentation support this procedure?"',
    '"Are these codes correct for a laparoscopic cholecystectomy?"',
    '"Is this procedure covered under our Aetna contract?"',
    '"What do we need to resolve before this surgery?"',
  ];

  return (
    <main className="grow flex flex-row bg-hex-gray-100 h-[calc(100%-6rem)]">
      <DropZoneWrapper
        className="h-full min-h-0 min-w-0 overflow-hidden flex flex-col items-center w-full"
        onFilesDropped={(files) => chatStore.setDroppedFiles(files as any)}
      >
        <Column
          sx={{
            height: "100%",
            width: "100%",
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden",
            justifyContent: "flex-start",
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: { xs: 1.5, sm: 2 },
              pb: { xs: 1, sm: 1.5 },
              boxSizing: "border-box",
            }}
          >
            <PreChatContainer containerClassName="min-w-0">
              <PreChatHeader
                icon={<PreServiceReviewIcon />}
                iconAlt="Pre-Service Review"
                title="Pre-Service Review"
                description="Upload your surgical scheduling documents by attaching the Scheduling Request Form, Clinical Records, and Prior Authorization Letter (if available) using the paperclip icon in the chatbox below or drag & drop them here."
              />

              <InfoCardGrid>
                <InfoCard title="I will help you with:">
                  <BulletList items={helpItems} />
                </InfoCard>
                <InfoCard title="Examples of appeal scenarios:">
                  <BulletList items={scenarios} />
                </InfoCard>
              </InfoCardGrid>
            </PreChatContainer>
          </Box>

          <ChatInput
            placeholder="Ask about surgical scheduling documents..."
            requiredInputs={channelsConfig["pre-service-review"].requiredInputs}
          />
        </Column>
      </DropZoneWrapper>
    </main>
  );
});

export default PreServiceReview;
